import { assertInArray, assertString } from '../../validators';

import type { CommonAttributeSettings, CommonMetadataKeys } from './common';
import { createAttributeSetter, DEFAULT_ATTRIBUTE_SETTINGS, defineMetadata } from './common';

type StringAttributeSettings = CommonAttributeSettings & {
    choices: readonly string[];
};
export const STRING_ATTRIBUTE_TYPE = 'StringAttributeType';
export type StringMetadataKeys = keyof StringAttributeSettings | CommonMetadataKeys;
const DEFAULT_SETTINGS: StringAttributeSettings = {
    ...DEFAULT_ATTRIBUTE_SETTINGS,
    choices: [],
};

/**
 * Creates an attribute decorator used to describe a string property. In addition to the shared
 * attribute settings, the string decorator allows validation to enforce the value is a member of inArray.
 */
export function StringAttribute<T extends string>(settings: Partial<StringAttributeSettings> = {}) {
    const resolvedSettings: StringAttributeSettings = { ...DEFAULT_SETTINGS, ...settings };
    const validator = (value: string) => {
        assertString(value);
        if (resolvedSettings.choices.length > 0) {
            assertInArray(value, resolvedSettings.choices);
        }
    };
    return function <This>(
        { get, set }: ClassAccessorDecoratorTarget<This, T>,
        context: ClassAccessorDecoratorContext,
    ): ClassAccessorDecoratorResult<This, T> {
        defineMetadata(
            resolvedSettings,
            STRING_ATTRIBUTE_TYPE,
            context,
            get as (this: unknown) => unknown,
        );

        return {
            get: function (this: This) {
                return get.call(this);
            },
            set: createAttributeSetter(
                validator,
                get as (this: object) => T,
                set as (this: object, value: T) => void,
            ) as (this: This, value: T) => void,
        };
    };
}
